'use client';

import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { FooterPaperEdge } from './footer-paper-edge';
import { cn } from '@/lib/utils';
import { OPEN_HOUR, CLOSE_HOUR } from '@/lib/business-hours';
import './footer.css';

interface FooterProps {
  className?: string;
}

const mainLinks = [
  { label: 'Меню', href: '/#menu' },
  { label: 'Акции', href: '/actions' },
  { label: 'Доставка', href: '/delivery' },
  { label: 'Оплата', href: '/payment' },
  { label: 'О нас', href: '/about' },
  { label: 'Вопросы и ответы', href: '/faq' },
];
const companyLinks = [
  { label: 'Отзывы', href: '/reviews' },
  { label: 'Блог', href: '/blog' },
  { label: 'Вакансии', href: '/vacancies' },
  { label: 'Арендодателям', href: '/rent' },
  { label: 'Аллергены', href: '/allergens' },
];
const legalLinks = [
  { label: 'Политика конфиденциальности', href: '/privacy' },
  { label: 'Публичная оферта', href: '/oferta' },
  { label: 'Безопасные платежи', href: '/secure-payments' },
  { label: 'Политика возврата', href: '/refund' },
];

const VKIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className || 'w-6 h-6'}
    style={style}
  >
    <path d="M15.07 2H8.93C3.33 2 2 3.33 2 8.93v6.14C2 20.67 3.33 22 8.93 22h6.14c5.6 0 6.93-1.33 6.93-6.93V8.93C22 3.33 20.67 2 15.07 2m3.08 14.27h-1.46c-.55 0-.72-.44-1.71-1.45-1-.97-1.43-1.1-1.68-1.1-.35 0-.45.1-.45.58v1.31c0 .41-.13.66-1.21.66-1.79 0-3.78-.97-5.18-2.77-2.1-2.95-2.67-5.16-2.67-5.61 0-.25.1-.48.58-.48h1.46c.44 0 .6.19.77.65.85 2.45 2.27 4.6 2.85 4.6.22 0 .32-.1.32-.66V9.13c-.07-1.14-.67-1.24-.67-1.65 0-.2.17-.41.44-.41h2.3c.37 0 .5.19.5.63v3.41c0 .37.17.5.27.5.22 0 .4-.13.81-.54 1.25-1.4 2.14-3.56 2.14-3.56.12-.25.32-.48.77-.48h1.46c.44 0 .54.23.44.63-.19.87-2.03 3.47-2.03 3.47-.16.26-.22.38 0 .67.16.22.69.67 1.04 1.08.65.72 1.15 1.33 1.28 1.75.14.41-.09.62-.52.62" />
  </svg>
);

const MaxIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="-100 -100 960 958"
    fill="currentColor"
    className={className}
    style={style}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M392.07 186.804C292.648 181.591 215.063 250.579 197.954 358.557C183.786 447.977 208.906 556.935 230.392 562.419C239.505 564.745 261.436 547.971 277.392 532.925C280.391 530.096 284.927 529.62 288.445 531.771C313.317 546.98 341.477 558.41 372.522 560.038C474.59 565.39 565.042 485.507 570.388 383.43C575.735 281.353 494.139 192.157 392.07 186.804ZM225.581 705.191C221.732 702.466 216.428 703.205 213.202 706.646C170.082 752.636 59.7277 784.896 54.6727 722.126C54.6727 672.946 43.6266 631.503 31.4646 585.873C16.5692 529.989 0 467.824 0 377.475C0 162.027 176.694 0 386.194 0C595.694 0 759.951 169.938 759.951 379.567C759.951 589.196 590.466 754.95 388.176 754.95C316.409 754.95 281.583 744.841 225.581 705.191Z"
    />
  </svg>
);

function ArrowIcon({ up = false }: { up?: boolean }) {
  return (
    <svg className="tear-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
      <path
        d={up ? 'm5 11 7-7 7 7M12 4v16' : 'M5 19 19 5M5 5h14v14'}
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function scrollToTop() {
  window.scrollTo({
    top: 0,
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
  });
}

export function Footer({ className }: FooterProps) {
  const footerRef = useRef<HTMLElement>(null);
  const workingHours = `${OPEN_HOUR}:00–${CLOSE_HOUR}:00`;

  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) return;
    let narrow: boolean | undefined;
    const syncNavigation = () => {
      const next = footer.clientWidth <= 560;
      if (next === narrow) return;
      narrow = next;
      footer.querySelectorAll<HTMLDetailsElement>('.tear-nav-group').forEach((group) => {
        group.open = !next;
        const summary = group.querySelector('summary');
        if (summary) summary.tabIndex = next ? 0 : -1;
      });
    };
    syncNavigation();
    const resize = new ResizeObserver(syncNavigation);
    resize.observe(footer);
    return () => resize.disconnect();
  }, []);

  return (
    <footer
      ref={footerRef}
      className={cn('azia-footer azia-footer-paper', className)}
      aria-label="Подвал сайта"
    >
      <div className="tear-upper">
        <div className="tear-head">
          <Link href="/" className="azia-footer-brand" aria-label="Азиаточка — на главную">
            <span className="azia-footer-wordmark" aria-hidden="true" />
          </Link>
          <span className="tear-city">Ростов-на-Дону</span>
          <button
            className="tear-top-button"
            type="button"
            onClick={scrollToTop}
            aria-label="Вернуться к началу"
          >
            <ArrowIcon up />
          </button>
        </div>
        <div className="tear-content">
          <div className="tear-nav-columns">
            {[
              { title: 'Заказ и помощь', links: mainLinks },
              { title: 'Азиаточка', links: companyLinks },
            ].map((group) => (
              <details className="tear-nav-group" open key={group.title}>
                <summary className="tear-label">
                  {group.title}
                  <svg
                    className="tear-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      d="M5 12h14M12 5v14"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                  </svg>
                </summary>
                <nav aria-label={group.title}>
                  {group.links.map((link) => (
                    <Link key={link.href} href={link.href}>
                      {link.label}
                    </Link>
                  ))}
                </nav>
              </details>
            ))}
          </div>
          <div className="tear-invite">
            <p className="tear-title">
              Роллы — всегда
              <br />
              хорошая идея.
            </p>
            <Link className="tear-order" href="/#menu">
              Выбрать роллы{' '}
              <span>
                <ArrowIcon />
              </span>
            </Link>
            <div className="tear-social-row">
              <nav className="tear-socials" aria-label="Социальные сети и поддержка">
                <a
                  href="https://vk.com/aziatochka_ru"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Азиаточка во ВКонтакте — откроется в новой вкладке"
                >
                  <VKIcon className="azia-footer-social-icon" />
                  <span>ВКонтакте</span>
                  <ArrowIcon />
                </a>
                <a
                  href="https://max.ru/u/f9LHodD0cOJy8oPBxUr-x-mis-JgEXUn4KSVirJM9Ix_OdGwXP4A3anpCCU"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Написать в MAX — откроется в новой вкладке"
                >
                  <MaxIcon className="azia-footer-social-icon" />
                  <span>MAX</span>
                  <ArrowIcon />
                </a>
              </nav>
            </div>
          </div>
        </div>
      </div>
      <FooterPaperEdge />
      <div className="tear-lower">
        <div className="tear-contact">
          <div>
            <span className="tear-label">На связи каждый день</span>
            <a className="tear-phone" href="tel:88633335050">
              8 (863) 333-50-50 <ArrowIcon />
            </a>
          </div>
          <p className="tear-hours">
            {workingHours}
            <span>Принимаем заказы</span>
          </p>
        </div>
        <nav className="tear-legal" aria-label="Правовая информация">
          {legalLinks.map((link) => (
            <Link key={link.href} href={link.href}>
              {link.label}
            </Link>
          ))}
        </nav>
        <div className="tear-copyright">
          <span>
            © Азиаточка, <span suppressHydrationWarning>{new Date().getFullYear()}</span>
          </span>
          <button className="tear-back-top" type="button" onClick={scrollToTop}>
            Наверх <ArrowIcon up />
          </button>
        </div>
      </div>
    </footer>
  );
}
